import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Input, Toggle } from "@/components/admin/ui";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, Upload, Image as ImageIcon } from "lucide-react";

// Plain text/URL settings fields. The image fields live in IMAGE_FIELDS below
// because they also need an upload control, a preview and a remove action.
const BUSINESS_FIELDS = [
  ["name", "Brand Name"], ["legalName", "Legal Business Name"], ["tagline", "Tagline"],
  ["phone", "Phone"], ["whatsapp", "WhatsApp"], ["email", "Email"],
  ["address", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
  ["storeHours", "Store Hours"],
  ["instagram", "Instagram URL"], ["whatsappChannel", "WhatsApp Channel URL"], ["instagramChannel", "Instagram Channel URL"],
  ["googleListing", "Google Business/Listing URL"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"],
];

// Every configurable *image* in Settings. `namespace` names the folder inside the
// existing private image storage (same endpoint the product/banner/category
// uploads use — no new storage provider, no R2 credentials in the browser).
const IMAGE_FIELDS = [
  { key: "logo", label: "Site Logo", hint: "Shown in the storefront header. PNG with transparency works best.", namespace: "settings-logo" },
  { key: "favicon", label: "Favicon", hint: "Browser tab icon (square PNG, e.g. 512×512).", namespace: "settings-favicon" },
  { key: "storePhoto", label: "Store Photo", hint: "Used on the home page and the About page.", namespace: "settings-store-photo" },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setS(data.settings); setIntegrations(data.integrations); });
  }, []);

  if (!s) return <div className="py-20 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  const setBiz = (k, v) => setS((p) => ({ ...p, business: { ...p.business, [k]: v } }));
  const setStore = (k, v) => setS((p) => ({ ...p, store: { ...p.store, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const store = s.store || {};

  return (
    <div>
      <AdminHeader title="Settings" subtitle="Business configuration & integrations" action={<Btn variant="accent" onClick={save} loading={saving} data-testid="save-settings-btn">Save Settings</Btn>} />

      <div className="space-y-6">
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Business Information</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {BUSINESS_FIELDS.map(([k, label]) => (
              <Input key={k} label={label} value={s.business?.[k] || ""} onChange={(e) => setBiz(k, e.target.value)} testid={`setting-${k}`} />
            ))}
          </div>
          <div className="mt-6 border-t border-slate-200 pt-5">
            <h4 className="font-semibold text-slate-900">Brand images</h4>
            <p className="text-xs text-slate-500 mt-0.5 mb-4">
              Upload a file (JPG or PNG, up to 10 MB) or keep using an image URL. Uploads go to the store's private image storage
              and are served through the site's media endpoint. Remember to press Save Settings.
            </p>
            <div className="grid md:grid-cols-3 gap-5">
              {IMAGE_FIELDS.map((f) => (
                <ImageField
                  key={f.key}
                  field={f}
                  value={s.business?.[f.key] || ""}
                  onChange={(v) => setBiz(f.key, v)}
                />
              ))}
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Store Configuration</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Currency" value={store.currency || "INR"} onChange={(e) => setStore("currency", e.target.value)} />
            <Input label="Low Stock Threshold" type="number" value={store.lowStockThreshold ?? 5} onChange={(e) => setStore("lowStockThreshold", Number(e.target.value))} />
            <Input label="Flat Shipping Rate (₹, blank = disabled)" type="number" value={store.flatShippingRate ?? ""} onChange={(e) => setStore("flatShippingRate", e.target.value === "" ? null : Number(e.target.value))} testid="setting-shipping-rate" />
            <Input label="Free Shipping Threshold (₹, blank = none)" type="number" value={store.freeShippingThreshold ?? ""} onChange={(e) => setStore("freeShippingThreshold", e.target.value === "" ? null : Number(e.target.value))} testid="setting-free-shipping" />
            <Input label="Tax Rate (%, blank = none)" type="number" value={store.taxRate ?? ""} onChange={(e) => setStore("taxRate", e.target.value === "" ? null : Number(e.target.value))} testid="setting-tax-rate" />
            <Input label="Handling Fee (₹, blank = none)" type="number" value={store.handlingFee ?? ""} onChange={(e) => setStore("handlingFee", e.target.value === "" ? null : Number(e.target.value))} testid="setting-handling-fee" />
          </div>
          <div className="flex flex-wrap gap-6 mt-4">
            <Toggle label="Cash on Delivery enabled" checked={!!store.codEnabled}
              onChange={(v) => setStore("codEnabled", v)} disabled={!integrations?.delhivery?.codSupported}
              testid="setting-cod" />
            <Toggle label="Store Pickup enabled" checked={!!store.storePickupEnabled} onChange={(v) => setStore("storePickupEnabled", v)} testid="setting-pickup" />
          </div>
          <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-3 mt-3">
            {integrations?.delhivery?.codSupported
              ? "COD uses the verified production Delhivery setup. Checkout only offers it for a serviceable PIN whose standard B2C response explicitly confirms COD."
              : "COD is unavailable until the verified production Delhivery token, pickup location, and explicit shipment-creation safety setting are present. Prepaid PIN serviceability does not establish COD availability."}
          </p>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-1">GST &amp; Tax</h3>
          <p className="text-xs text-slate-500 mb-4">
            Enable GST to charge CGST/SGST (same state) or IGST (other state) per item. Leave disabled to keep the single flat tax rate above.
          </p>
          <div className="flex flex-wrap gap-6 mb-4">
            <Toggle label="GST enabled" checked={!!store.gstEnabled} onChange={(v) => setStore("gstEnabled", v)} testid="setting-gst-enabled" />
            <Toggle label="Prices include GST" checked={!!store.priceIncludesTax} onChange={(v) => setStore("priceIncludesTax", v)} testid="setting-gst-inclusive" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Seller GSTIN" value={store.gstin || ""} onChange={(e) => setStore("gstin", e.target.value)} testid="setting-gstin" />
            <Input label="Registered GST state" value={store.gstState || ""} onChange={(e) => setStore("gstState", e.target.value)} testid="setting-gst-state" />
            <Input label="Default GST rate (%)" type="number" value={store.defaultGstRate ?? ""} onChange={(e) => setStore("defaultGstRate", e.target.value === "" ? null : Number(e.target.value))} testid="setting-gst-rate" />
            <Input label="Shipping GST rate (%)" type="number" value={store.shippingGstRate ?? ""} onChange={(e) => setStore("shippingGstRate", e.target.value === "" ? null : Number(e.target.value))} testid="setting-shipping-gst" />
          </div>
          <p className="text-[11px] text-slate-400 mt-3">HSN codes and per-item GST rates are set on each product/variant. The backend recalculates all tax at checkout; the browser value is never trusted.</p>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-1">Cancellation &amp; Returns policy</h3>
          <p className="text-xs text-slate-500 mb-4">Applied server-side. A product with its own return policy overrides the default below.</p>
          <div className="flex flex-wrap gap-6 mb-4">
            <Toggle label="Returns allowed by default" checked={!!store.defaultReturnPolicy?.allowed} onChange={(v) => setStore("defaultReturnPolicy", { ...(store.defaultReturnPolicy || {}), allowed: v })} testid="setting-returns-allowed" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Default return window (days)" type="number" value={store.defaultReturnPolicy?.windowDays ?? ""} onChange={(e) => setStore("defaultReturnPolicy", { ...(store.defaultReturnPolicy || {}), windowDays: e.target.value === "" ? null : Number(e.target.value) })} testid="setting-return-days" />
            <Input label="Refund point (ON_APPROVAL / ON_RECEIPT)" value={store.refundPoint || "ON_RECEIPT"} onChange={(e) => setStore("refundPoint", e.target.value)} testid="setting-refund-point" />
            <Input label="Cancellation note / policy" value={store.cancellationPolicy?.notes || ""} onChange={(e) => setStore("cancellationPolicy", { ...(store.cancellationPolicy || {}), notes: e.target.value })} testid="setting-cancel-notes" />
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Integrations</h3>
          <p className="text-sm text-slate-500 mb-4">API secrets are stored server-side in environment variables only — never in the database or frontend.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <IntegrationRow name="Razorpay (Payments)" configured={integrations?.razorpay?.configured} env="RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET" />
            <IntegrationRow name="Delhivery (Shipping)" configured={integrations?.delhivery?.configured} env="DELHIVERY_API_TOKEN" />
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * One configurable site image: preview, file upload (PNG/JPG), replace, remove,
 * and the manual URL field the settings already had.
 *
 * The upload reuses the existing authorized admin image endpoint and private R2
 * storage — the browser only ever receives the resulting media reference. A failed
 * upload leaves the current image/URL exactly as it was, so the live logo can
 * never be wiped by a network error.
 */
function ImageField({ field, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPG/PNG images are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be 10 MB or smaller");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("productId", field.namespace);
      const { data } = await api.post("/admin/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);                    // same field shape as a pasted URL
      toast.success(`${field.label} uploaded — press Save Settings to publish it`);
    } catch (e) {
      const message = apiError(e);
      setError(message);                     // nothing is cleared: the current image stays
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{field.label}</label>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid={`setting-${field.key}-file`} />
      <div className="flex items-center gap-3 mt-1">
        {value ? (
          <img src={resolveMediaUrl(value)} alt="" onError={handleMediaError} data-testid={`setting-${field.key}-preview`}
            className="h-16 w-24 rounded-lg bg-slate-50 object-contain border border-slate-200" />
        ) : (
          <div className="h-16 w-24 rounded-lg bg-slate-50 border border-dashed border-slate-300 grid place-items-center text-slate-400"
            data-testid={`setting-${field.key}-placeholder`}>
            <ImageIcon className="h-5 w-5" />
          </div>
        )}
        <div className="flex flex-col items-start gap-1.5">
          <Btn type="button" variant="outline" onClick={() => fileRef.current?.click()} loading={uploading}
            data-testid={`setting-${field.key}-upload-btn`}>
            <Upload className="h-4 w-4" /> {value ? "Replace" : "Upload"}
          </Btn>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-xs text-red-600 hover:underline"
              data-testid={`setting-${field.key}-remove`}>
              Remove
            </button>
          )}
        </div>
      </div>
      {error && <p className="text-xs text-red-600 mt-1.5" data-testid={`setting-${field.key}-error`}>{error}</p>}
      <p className="text-[11px] text-slate-400 mt-1.5">{field.hint}</p>
      <Input label="Or paste an image URL" value={value} onChange={(e) => onChange(e.target.value)} testid={`setting-${field.key}`} />
    </div>
  );
}

function IntegrationRow({ name, configured, env }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <p className="font-medium text-slate-900">{name}</p>
        {configured
          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Connected</span>
          : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><AlertCircle className="h-4 w-4" /> Not Configured</span>}
      </div>
      <p className="text-xs text-slate-400 font-mono mt-1">{env}</p>
    </div>
  );
}
